/* ============================================================
   Flow Wake-Up Quiz - Haptics

   Android browser haptics only. iOS/Safari web haptics are intentionally
   unsupported because the Vibration API is unavailable there.
   ============================================================ */

(function () {
  if (typeof window === 'undefined') return;

  var reduceMotion = false;
  var hapticsDebug = false;
  try {
    reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    hapticsDebug = new URLSearchParams(window.location.search).has('flow_haptics_debug');
  } catch (error) {
    reduceMotion = false;
    hapticsDebug = false;
  }

  var defaultPatterns = {
    success: [30, 60, 40],
    warning: [40, 100, 40],
    error: [40, 40, 40, 40, 40],
    light: [15],
    medium: [25],
    heavy: [35],
    soft: [40],
    rigid: [10],
    selection: [8],
    nudge: [80, 80, 50],
    buzz: [1000]
  };

  var defaults = {
    selection: { minInterval: 70, reduceMotion: true },
    tap:       { minInterval: 80, reduceMotion: true },
    advance:   { minInterval: 120, reduceMotion: true },
    back:      { minInterval: 120, reduceMotion: true },
    success:   { minInterval: 220, reduceMotion: true },
    error:     { minInterval: 220, reduceMotion: true },
    warning:   { minInterval: 220, reduceMotion: true },
    motion:    { minInterval: reduceMotion ? 500 : 140, reduceMotion: false },
    milestone: { minInterval: reduceMotion ? 600 : 180, reduceMotion: false }
  };

  var methodInputs = {
    selection: 'selection',
    tap: 'medium',
    advance: 'medium',
    back: 'light',
    success: 'success',
    error: 'error',
    warning: 'warning',
    motion: 'nudge',
    milestone: 'selection'
  };

  var directSelector = [
    '.option',
    '.btn',
    '.back-btn',
    '.slider-answer__input',
    '.unlock__cell',
    '.scratch__cover',
    '.alarm-sound-row',
    '.lp-top__cta',
    '.buy__cta',
    '.buy__addon',
    '.buy__addon input',
    '.lp-foot a'
  ].join(',');

  var lastByKey = {};
  var directGestureBound = false;
  var pointerTriggeredElements = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
  var directSuppressUntil = 0;
  var directSuppressWindowMs = 280;

  var diagnostics = {
    readyAt: Date.now(),
    debug: hapticsDebug,
    android: isAndroidDevice(),
    nativeVibrate: hasNativeVibrate(),
    enabled: isEnabled(),
    triggerCount: 0,
    directTriggerCount: 0,
    dedupedCount: 0,
    lastAttempt: null,
    lastDirectAttempt: null
  };

  function hasNativeVibrate() {
    return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
  }

  function isAndroidDevice() {
    if (typeof navigator === 'undefined') return false;

    var userAgent = navigator.userAgent || '';
    var platform = navigator.platform || '';
    if (navigator.userAgentData && navigator.userAgentData.platform) {
      platform += ' ' + navigator.userAgentData.platform;
    }

    return /Android/i.test(userAgent + ' ' + platform);
  }

  function isEnabled() {
    return isAndroidDevice() && hasNativeVibrate();
  }

  function normalizeNumber(value, max) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
    return Math.min(max, Math.round(value));
  }

  function appendStep(output, step) {
    if (!step) return;

    var delay = normalizeNumber(step.delay || 0, 1000);
    var duration = normalizeNumber(step.duration || 0, 1000);
    if (delay === null || duration === null) return;

    if (delay > 0) {
      if (output.length > 0 && output.length % 2 === 0) {
        output[output.length - 1] += delay;
      } else {
        if (!output.length) output.push(0);
        output.push(delay);
      }
    }

    if (duration > 0) output.push(duration);
  }

  function normalizePattern(input) {
    if (input == null) return defaultPatterns.medium.slice();

    if (typeof input === 'string') {
      return defaultPatterns[input] ? defaultPatterns[input].slice() : null;
    }

    if (typeof input === 'number') {
      var duration = normalizeNumber(input, 1000);
      return duration && duration > 0 ? [duration] : null;
    }

    if (Array.isArray(input)) {
      if (!input.length) return null;

      if (typeof input[0] === 'number') {
        var numericPattern = input
          .map(function (part) { return normalizeNumber(part, 1000); })
          .filter(function (part) { return part !== null; });
        return numericPattern.length ? numericPattern : null;
      }

      var output = [];
      input.forEach(function (step) { appendStep(output, step); });
      return output.length ? output : null;
    }

    if (input && Array.isArray(input.pattern)) {
      return normalizePattern(input.pattern);
    }

    return null;
  }

  function applyReducedMotion(pattern, options) {
    if (!reduceMotion || (options && options.reduceMotion === false)) return pattern;

    return pattern.map(function (part, index) {
      return index % 2 === 0 ? Math.min(part, 20) : part;
    });
  }

  function toNativePattern(input, options) {
    var pattern = normalizePattern(input);
    return pattern ? applyReducedMotion(pattern, options || {}) : null;
  }

  function closest(target, selector) {
    if (!target || typeof document === 'undefined') return null;
    if (target.nodeType !== 1) target = target.parentElement;
    if (!target || typeof target.closest !== 'function') return null;
    return target.closest(selector);
  }

  function isDisabledTarget(target) {
    var disabled = closest(target, '[disabled], [aria-disabled="true"]');
    return !!(disabled && closest(disabled, directSelector));
  }

  function getTargetLabel(target) {
    if (!target) return null;
    var className = typeof target.className === 'string' ? target.className : '';
    return [
      target.tagName ? target.tagName.toLowerCase() : 'node',
      target.id ? '#' + target.id : '',
      className ? '.' + className.trim().replace(/\s+/g, '.') : ''
    ].join('');
  }

  function shouldThrottle(key, minInterval) {
    var now = Date.now();
    var last = lastByKey[key] || 0;
    if (now - last < minInterval) return true;
    lastByKey[key] = now;
    return false;
  }

  function shouldDedupeAfterDirect(opts) {
    if (opts && opts.source === 'direct') return false;
    return Date.now() < directSuppressUntil;
  }

  function updateDiagnostics(method, key, nativePattern, result, throttled, error, meta) {
    var source = meta && meta.source ? meta.source : 'api';
    var route = meta && meta.route ? meta.route : (throttled ? 'throttled' : (result ? 'native' : 'unsupported'));

    diagnostics.android = isAndroidDevice();
    diagnostics.nativeVibrate = hasNativeVibrate();
    diagnostics.enabled = isEnabled();
    diagnostics.triggerCount += throttled ? 0 : 1;
    if (source === 'direct' && !throttled) diagnostics.directTriggerCount += 1;
    if (meta && meta.deduped) diagnostics.dedupedCount += 1;
    diagnostics.lastAttempt = {
      at: Date.now(),
      method: method,
      key: key,
      source: source,
      route: route,
      result: !!result,
      throttled: !!throttled,
      deduped: !!(meta && meta.deduped),
      directTarget: meta && meta.directTarget ? meta.directTarget : null,
      nativePattern: nativePattern ? nativePattern.slice() : null,
      error: error ? String(error && error.message ? error.message : error) : null
    };
    if (source === 'direct') diagnostics.lastDirectAttempt = diagnostics.lastAttempt;
    if (hapticsDebug && typeof console !== 'undefined' && console.debug) {
      console.debug('[Flow quiz haptics]', diagnostics.lastAttempt);
    }
  }

  function trigger(input, options) {
    var opts = options || {};
    var method = opts.method || (typeof input === 'string' ? input : 'custom');
    var key = opts.key || method;
    var minInterval = typeof opts.minInterval === 'number' ? opts.minInterval : 0;
    var nativePattern = toNativePattern(input, opts);

    if (!nativePattern || !nativePattern.length) {
      updateDiagnostics(method, key, nativePattern, false, false, 'No valid haptic pattern', opts);
      return false;
    }

    if (!isEnabled()) {
      updateDiagnostics(method, key, nativePattern, false, false, null, Object.assign({}, opts, {
        route: 'unsupported'
      }));
      return false;
    }

    if (shouldDedupeAfterDirect(opts)) {
      updateDiagnostics(method, key, nativePattern, false, false, null, Object.assign({}, opts, {
        route: 'direct-dedupe',
        deduped: true
      }));
      return false;
    }

    if (minInterval && shouldThrottle(key, minInterval)) {
      updateDiagnostics(method, key, nativePattern, false, true, null, opts);
      return false;
    }

    try {
      var result = navigator.vibrate(nativePattern) !== false;
      updateDiagnostics(method, key, nativePattern, result, false, null, opts);
      return result;
    } catch (error) {
      updateDiagnostics(method, key, nativePattern, false, false, error, Object.assign({}, opts, {
        route: 'error'
      }));
      return false;
    }
  }

  function triggerMethod(name, options) {
    var opts = Object.assign({}, defaults[name] || {}, options || {});
    opts.key = opts.key || name;
    opts.method = name;
    return trigger(methodInputs[name] || 'medium', opts);
  }

  function directMethodForTarget(target) {
    if (!target || isDisabledTarget(target)) return null;
    if (closest(target, '.back-btn')) return 'back';
    if (closest(target, '.option')) return 'selection';
    if (closest(target, '.slider-answer__input')) return 'selection';
    if (closest(target, '.unlock__claim')) return 'success';
    if (closest(target, '.unlock__cell')) return 'advance';
    if (closest(target, '.scratch__cover')) return 'selection';
    if (closest(target, '.alarm-sound-row')) return 'advance';
    if (closest(target, '.buy__cta')) return 'success';
    if (closest(target, '.buy__addon, .buy__addon input')) return 'selection';
    if (closest(target, '.lp-top__cta')) return 'advance';
    if (closest(target, '.lp-foot a')) return 'selection';
    if (closest(target, '.btn--text')) return 'selection';
    if (closest(target, '.btn')) return 'advance';
    return null;
  }

  function directKeyForTarget(target, method) {
    if (closest(target, '.option')) return 'direct-option';
    if (closest(target, '.slider-answer__input')) return 'direct-slider';
    if (closest(target, '.unlock__cell')) return 'direct-unlock-card';
    if (closest(target, '.scratch__cover')) return 'direct-scratch';
    if (closest(target, '.alarm-sound-row')) return 'direct-alarm-sound';
    if (closest(target, '.buy__cta')) return 'direct-lp-checkout';
    if (closest(target, '.buy__addon, .buy__addon input')) return 'direct-lp-addon';
    return 'direct-' + method;
  }

  function markPointerTriggered(element) {
    if (!pointerTriggeredElements || !element) return;
    pointerTriggeredElements.add(element);
    window.setTimeout(function () {
      pointerTriggeredElements.delete(element);
    }, 1000);
  }

  function wasPointerTriggered(element) {
    if (!pointerTriggeredElements || !element) return false;
    if (!pointerTriggeredElements.has(element)) return false;
    pointerTriggeredElements.delete(element);
    return true;
  }

  function triggerDirect(target, source) {
    var method = directMethodForTarget(target);
    if (!method) return false;

    directSuppressUntil = Date.now() + directSuppressWindowMs;
    return triggerMethod(method, {
      key: directKeyForTarget(target, method),
      minInterval: method === 'selection' ? 45 : 80,
      source: 'direct',
      directTarget: getTargetLabel(target),
      directEvent: source
    });
  }

  function bindDirectGestureHaptics() {
    if (directGestureBound || typeof document === 'undefined' || !isEnabled()) return;
    directGestureBound = true;

    document.addEventListener('pointerdown', function (event) {
      var target = closest(event.target, directSelector);
      if (!target) return;
      markPointerTriggered(target);
      triggerDirect(target, 'pointerdown');
    }, { capture: true, passive: true });

    document.addEventListener('click', function (event) {
      var target = closest(event.target, directSelector);
      if (!target) return;
      if (wasPointerTriggered(target)) return;
      triggerDirect(target, 'click');
    }, true);
  }

  bindDirectGestureHaptics();

  if (hapticsDebug && typeof console !== 'undefined' && console.debug) {
    console.debug('[Flow quiz haptics] ready', {
      enabled: isEnabled(),
      android: isAndroidDevice(),
      nativeVibrate: hasNativeVibrate()
    });
  }

  window.FlowHaptics = {
    isSupported: isEnabled(),
    version: 'android-vibrate-1',
    webHapticsVersion: null,
    trigger: trigger,
    cancel: function () {
      if (!isEnabled()) return;
      try { navigator.vibrate(0); } catch (error) {}
    },
    getDiagnostics: function () {
      diagnostics.android = isAndroidDevice();
      diagnostics.nativeVibrate = hasNativeVibrate();
      diagnostics.enabled = isEnabled();
      return Object.assign({}, diagnostics, {
        lastAttempt: diagnostics.lastAttempt ? Object.assign({}, diagnostics.lastAttempt) : null,
        lastDirectAttempt: diagnostics.lastDirectAttempt ? Object.assign({}, diagnostics.lastDirectAttempt) : null
      });
    },
    selection: function (options) { return triggerMethod('selection', options); },
    tap: function (options) { return triggerMethod('tap', options); },
    advance: function (options) { return triggerMethod('advance', options); },
    back: function (options) { return triggerMethod('back', options); },
    success: function (options) { return triggerMethod('success', options); },
    error: function (options) { return triggerMethod('error', options); },
    warning: function (options) { return triggerMethod('warning', options); },
    motion: function (options) { return triggerMethod('motion', options); },
    milestone: function (options) { return triggerMethod('milestone', options); }
  };
})();
